import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft } from 'lucide-react';
import { LEGAL_VERSIONS } from '@/constants/legal';

const Auth = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user, loading, signIn, signUp, resetPassword, updatePassword, isPasswordRecovery, clearRecoveryMode, signOut } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // CRITICAL: Check recovery mode BEFORE checking auth to prevent premature redirect
  // When in recovery mode, user has a valid session but needs to set new password
  if (isPasswordRecovery) {
    return <PasswordRecoveryForm 
      t={t} 
      toast={toast} 
      updatePassword={updatePassword} 
      clearRecoveryMode={clearRecoveryMode}
      signOut={signOut}
    />;
  }

  // Only redirect if we have a valid, fresh session (not just a cached user)
  if (user && !loading && user.aud === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const { error } = await signIn(email, password);

    if (error) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message,
      });
    }

    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('reset-email') as string;

    const { error } = await resetPassword(email);

    if (error) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message,
      });
    } else {
      toast({
        title: t('common.success'),
        description: t('auth.resetPasswordSent'),
      });
      setShowForgotPassword(false);
    }

    setIsLoading(false);
  };


  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!termsAccepted) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: 'You must accept the Terms of Service and Privacy Policy to create an account.',
      });
      return;
    }

    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    const firstName = formData.get('firstName') as string;
    const lastName = formData.get('lastName') as string;
    const companyName = formData.get('companyName') as string;
    const accessCode = (formData.get('accessCode') as string) || '';

    if (password !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: 'Passwords do not match',
      });
      setIsLoading(false);
      return;
    }

    const { error, data } = await signUp(email, password, firstName, lastName, companyName, accessCode || undefined);

    if (error) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: error.message,
      });
    } else {
      // Detect repeated signup: Supabase returns identities: [] for existing accounts
      const isRepeatedSignup = data?.user?.identities?.length === 0;

      if (isRepeatedSignup) {
        toast({
          variant: 'destructive',
          title: 'Account already exists',
          description: 'This email is already registered. Please sign in instead, or use "Forgot password?" if you need to reset your credentials.',
        });
      } else {
        // Record legal consent — best-effort (backup is tos_accepted_at in user_metadata)
        const userId = data?.user?.id;
        if (userId) {
          const now = new Date().toISOString();
          supabase.from('legal_consents').insert([
            { user_id: userId, document_type: 'tos', document_version: LEGAL_VERSIONS.tos, accepted_at: now },
            { user_id: userId, document_type: 'privacy_policy', document_version: LEGAL_VERSIONS.privacy_policy, accepted_at: now },
          ]).then(({ error: consentError }) => {
            if (consentError) console.warn('[Legal] Failed to record consent:', consentError.message);
          });
        }

        toast({
          title: t('common.success'),
          description: 'Account created successfully! Please check your email to verify your account.',
        });
      }
    }

    setIsLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to home
          </Link>
          <h1 className="text-3xl font-bold">Workforce</h1>
          <p className="text-muted-foreground mt-2">Employee Management Platform</p>
        </div>

        <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') === 'signup' ? 'signup' : 'signin'} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signin">{t('auth.signIn')}</TabsTrigger>
            <TabsTrigger value="signup">{t('auth.signUp')}</TabsTrigger>
          </TabsList>

          <TabsContent value="signin">
            <Card>
              <CardHeader>
                <CardTitle>{showForgotPassword ? t('auth.forgotPassword') : t('auth.signIn')}</CardTitle>
                <CardDescription>
                  {showForgotPassword 
                    ? 'Enter your email to receive a password reset link'
                    : 'Enter your credentials to access your account'}
                </CardDescription>
              </CardHeader>
              {showForgotPassword ? (
                <form onSubmit={handleForgotPassword}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">{t('auth.email')}</Label>
                      <Input
                        id="reset-email"
                        name="reset-email"
                        type="email"
                        required
                        placeholder="you@company.com"
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col gap-2">
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? t('common.loading') : t('auth.sendResetLink')}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="text-sm text-muted-foreground hover:underline"
                    >
                      {t('auth.backToSignIn')}
                    </button>
                  </CardFooter>
                </form>
              ) : (
              <form onSubmit={handleSignIn}>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">{t('auth.email')}</Label>
                    <Input
                      id="signin-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">{t('auth.password')}</Label>
                    <Input
                      id="signin-password"
                      name="password"
                      type="password"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </button>
                </CardContent>
                <CardFooter>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? t('common.loading') : t('auth.signInButton')}
                  </Button>
                </CardFooter>
              </form>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="signup">
            <Card>
              <CardHeader>
                <CardTitle>{t('auth.signUp')}</CardTitle>
                <CardDescription>
                  Create your account and company profile
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleSignUp}>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t('auth.firstName')}</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        required
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t('auth.lastName')}</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        required
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="companyName">{t('auth.companyName')}</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      required
                      placeholder="Your Company Ltd."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">{t('auth.email')}</Label>
                    <Input
                      id="signup-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">{t('auth.password')}</Label>
                    <Input
                      id="signup-password"
                      name="password"
                      type="password"
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="accessCode" className="text-muted-foreground text-xs">
                      Access Code (optional)
                    </Label>
                    <Input
                      id="accessCode"
                      name="accessCode"
                      type="text"
                      placeholder="Enter access code if you have one"
                      maxLength={50}
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="flex items-start space-x-2 pt-2">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                      className="mt-0.5"
                    />
                    <label htmlFor="terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" className="text-primary underline hover:text-primary/80">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" target="_blank" className="text-primary underline hover:text-primary/80">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button type="submit" className="w-full" disabled={isLoading || !termsAccepted}>
                    {isLoading ? t('common.loading') : t('auth.signUpButton')}
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Separate component for password recovery form
interface PasswordRecoveryFormProps {
  t: (key: string) => string;
  toast: ReturnType<typeof import('@/hooks/use-toast').useToast>['toast'];
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
  clearRecoveryMode: () => void;
  signOut: () => Promise<{ error: unknown }>;
}

const PasswordRecoveryForm = ({ t, toast, updatePassword, clearRecoveryMode, signOut }: PasswordRecoveryFormProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdatePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    const newPassword = formData.get('new-password') as string;
    const confirmPassword = formData.get('confirm-password') as string;

    // Client-side validation
    if (newPassword.length < 6) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('auth.passwordTooShort'),
      });
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: t('auth.passwordMismatch'),
      });
      setIsLoading(false);
      return;
    }

    const { error } = await updatePassword(newPassword);

    if (error) {
      // Check for expired token
      if (error.message?.includes('expired') || error.message?.includes('invalid')) {
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: t('auth.recoveryLinkExpired'),
        });
        clearRecoveryMode();
      } else {
        toast({
          variant: 'destructive',
          title: t('common.error'),
          description: error.message,
        });
      }
    } else {
      toast({
        title: t('common.success'),
        description: t('auth.passwordUpdated'),
      });
      // Sign out to force fresh login with new password
      await signOut();
    }

    setIsLoading(false);
  };

  const handleCancel = async () => {
    clearRecoveryMode();
    await signOut();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">Workforce</h1>
          <p className="text-muted-foreground mt-2">Employee Management Platform</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('auth.setNewPassword')}</CardTitle>
            <CardDescription>
              {t('auth.setNewPasswordDescription')}
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleUpdatePassword}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
                <Input
                  id="new-password"
                  name="new-password"
                  type="password"
                  required
                  minLength={6}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('auth.confirmNewPassword')}</Label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  required
                  minLength={6}
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? t('common.loading') : t('auth.updatePassword')}
              </Button>
              <button
                type="button"
                onClick={handleCancel}
                className="text-sm text-muted-foreground hover:underline"
              >
                {t('common.cancel')}
              </button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
